import OfferInclusionsList from 'components/Common/Inclusions/OfferInclusionsList'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import Modal from 'LuxKit/Modal/Modal'
import BedbankCancellationPolicy from 'components/OfferPage/Bedbank/BedbankCancellationPolicy'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import { useAppSelector } from 'hooks/reduxHooks'
import { useDirectSearchPrices } from 'hooks/Search/useSearchPrices'
import useBedbankOfferSearchPricing from 'hooks/useBedbankOfferSearchPricing'
import { EmptyObject } from 'lib/object/objectUtils'
import determineOfferFeatureSymbol from 'lib/offer/determineOfferFeatureSymbol'
import { getHotelOfferUrl } from 'lib/offer/offerPageURL'
import { buildBedbankCancellationPolicy } from 'lib/search/hotels'
import LuxLPPPricingLabel from 'luxPlus/components/LuxLPPPricingLabel'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import React, { useCallback, useContext, useMemo } from 'react'
import ExperimentalSearchTileFeaturesList
  from 'components/SearchKit/SearchTiles/shared/ExperimentalSearchTileFeaturesList'

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  filters?: App.OfferListFilters
}

function ExperimentalBedbankSearchTileFeatures({ offer, filters = EmptyObject }: Props) {
  const {
    offerPrice,
    offerMemberPrice,
    shownRate,
  } = useBedbankOfferSearchPricing(offer, filters)
  const offerMetaData = useOfferMetaData(offer.id, filters)
  const cancellationPolicy = useMemo(() => {
    return buildBedbankCancellationPolicy(
      offerMetaData?.cancellationPolicyInfo,
      filters,
      offer.property.timezone,
    )
  }, [offerMetaData?.cancellationPolicyInfo, filters, offer])
  const amenities = useMemo<Array<App.OfferInclusion>>(() => {
    return offer.popularFacilities.map(facility => ({
      description: facility,
      id: facility,
      symbol: determineOfferFeatureSymbol(facility),
    }))
  }, [offer])

  const luxPlusLPPEnabled = useAppSelector(getIsLuxPlusLPPEnabled)
  const directSearchPrices = useDirectSearchPrices({ filters, offerId: offer.id })
  const showModal = useContext(ModalContext)
  const openAllInclusionsModal = useCallback(() => {
    showModal(<Modal
      title={offer.property.name}
      primaryActionTo={getHotelOfferUrl(offer.id, offer.type)}
      primaryActionText="View offer"
    >
      <OfferInclusionsList inclusions={amenities} />
    </Modal>)
  }, [showModal, offer, amenities])

  const savingAmount = offerMetaData?.pricing?.lowestMemberPrice && offerMetaData?.pricing?.lowestPrice ?
    offerMetaData?.pricing?.lowestPrice - offerMetaData?.pricing?.lowestMemberPrice :
    undefined

  return <Group direction="vertical" gap={8} noDisplayWhenEmpty>
    <ExperimentalSearchTileFeaturesList
      features={amenities}
      featuresType="amenities"
      cancellationPolicy={(offer.productType === 'limited_time_special' && (shownRate || cancellationPolicy)) && <BedbankCancellationPolicy
        icon={<LineCalendarIcon />}
        rate={shownRate ?? cancellationPolicy}
        offer={offer}
        showNonCancellation
      />}
      onMoreClick={openAllInclusionsModal}
    />
    {luxPlusLPPEnabled && !!offerMetaData?.pricing?.lowestMemberPrice && !!savingAmount && savingAmount > 0 && <LuxLPPPricingLabel
      offer={offer}
      hasLuxPlusRate={directSearchPrices?.hasLuxPlusRate}
      offerMemberPrice={offerMemberPrice}
      offerPrice={offerPrice}
      savingAmount={savingAmount}
    />}
  </Group>
}

export default ExperimentalBedbankSearchTileFeatures
